import React, {useState} from 'react'
import {ModalBase} from '../Modals/ModalBase'
import {
  TIPOS_AYUDA,
  TIPOS_DOCUMENTO,
  TIPOS_CON_DOCUMENTO,
  ClienteContexto,
  FormContacto,
  TipoContacto,
  buildWhatsAppMessage,
  getWhatsAppConfig,
  openWhatsApp,
  getEmailGerencia,
  openMailtoGerencia,
} from '../../utils/whatsapp.utils'

interface Props {
  tipo: TipoContacto
  contexto: ClienteContexto
  onClose: () => void
  /** Hook para fase 2: se llama antes de abrir WhatsApp (p.ej. persistir en backend) */
  onSubmit?: (data: {tipo: TipoContacto; contexto: ClienteContexto; form: FormContacto}) => Promise<void>
}

const TITULOS: Record<TipoContacto, string> = {
  ventas: 'Hablar con Ventas',
  soporte: 'Solicitar Soporte',
  gerencia: 'Escalar con Gerencia',
}

const LABEL_TIPO_AYUDA: Record<TipoContacto, string> = {
  ventas: 'Interés o consulta',
  soporte: 'Tipo de ayuda',
  gerencia: 'Motivo del escalamiento',
}

interface FormErrors {
  tipoAyuda: string
  mensajeAdicional: string
}

const ModalContacto = ({tipo, contexto, onClose, onSubmit}: Props) => {
  const [form, setForm] = useState<FormContacto>({tipoAyuda: '', mensajeAdicional: '', tipoDocumento: ''})
  const [errores, setErrores] = useState<FormErrors>({tipoAyuda: '', mensajeAdicional: ''})
  const [enviando, setEnviando] = useState(false)

  const mostrarSelectorDocumento = TIPOS_CON_DOCUMENTO.includes(form.tipoAyuda)

  const handleChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target
    setForm(prev => ({
      ...prev,
      [name]: value,
      // Limpiar documento si cambia el tipo de ayuda a uno que no lo requiere
      ...(name === 'tipoAyuda' && !TIPOS_CON_DOCUMENTO.includes(value) ? {tipoDocumento: ''} : {}),
    }))
    if (value && name !== 'tipoDocumento')
      setErrores(prev => ({...prev, [name]: ''}))
  }

  const validar = (): boolean => {
    const nuevosErrores: FormErrors = {tipoAyuda: '', mensajeAdicional: ''}
    if (!form.tipoAyuda) nuevosErrores.tipoAyuda = 'Este campo es requerido.'
    if (!form.mensajeAdicional.trim()) nuevosErrores.mensajeAdicional = 'Este campo es requerido.'
    setErrores(nuevosErrores)
    return !nuevosErrores.tipoAyuda && !nuevosErrores.mensajeAdicional
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validar()) return

    setEnviando(true)
    try {
      if (onSubmit) {
        await onSubmit({tipo, contexto, form})
      }

      if (tipo === 'gerencia') {
        const emailTo = getEmailGerencia()
        openMailtoGerencia(emailTo, contexto, form)
        onClose()
      } else {
        const config = getWhatsAppConfig()
        const phoneMap: Record<'ventas' | 'soporte', string> = {
          ventas: config.ventas,
          soporte: config.soporte,
        }
        const message = buildWhatsAppMessage(tipo, contexto, form)
        await openWhatsApp(phoneMap[tipo], message)
        onClose()
      }
    } finally {
      setEnviando(false)
    }
  }

  const labelBoton = enviando
    ? 'Abriendo...'
    : tipo === 'gerencia'
    ? 'Redactar correo'
    : 'Abrir WhatsApp'

  return (
    <ModalBase>
      <div className='modal-card'>
        <div className='modal-content'>
          <div className='w-layout-hflex'>
            <h3 className='card-title l'>{TITULOS[tipo]}</h3>
          </div>
          <div className='form-separator'></div>
          <form onSubmit={handleSubmit} autoComplete='off'>
            <div className='label-input'>
              <label className='field-label m'>
                {LABEL_TIPO_AYUDA[tipo]} <span style={{color: 'red'}}>*</span>
              </label>
              <select
                name='tipoAyuda'
                className='m text-field w-input'
                value={form.tipoAyuda}
                onChange={handleChange}
                style={{height: '47px', cursor: 'pointer'}}
              >
                <option value=''>Seleccione una opción</option>
                {TIPOS_AYUDA.map(t => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {errores.tipoAyuda && <p className='alert'>{errores.tipoAyuda}</p>}
            </div>

            {mostrarSelectorDocumento && (
              <div className='label-input' style={{marginTop: '16px'}}>
                <label className='field-label m'>Tipo de documento</label>
                <select
                  name='tipoDocumento'
                  className='m text-field w-input'
                  value={form.tipoDocumento}
                  onChange={handleChange}
                  style={{height: '47px', cursor: 'pointer'}}
                >
                  <option value=''>Seleccione un documento (opcional)</option>
                  {TIPOS_DOCUMENTO.map(doc => (
                    <option key={doc} value={doc}>
                      {doc}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className='label-input' style={{marginTop: '16px'}}>
              <label className='field-label m'>
                Mensaje adicional <span style={{color: 'red'}}>*</span>
              </label>
              <textarea
                name='mensajeAdicional'
                className='m text-field w-input'
                value={form.mensajeAdicional}
                onChange={handleChange}
                rows={4}
                placeholder='Describe con detalle tu solicitud...'
                style={{resize: 'vertical', minHeight: '100px', padding: '8px'}}
              />
              {errores.mensajeAdicional && <p className='alert'>{errores.mensajeAdicional}</p>}
            </div>

            <div className='form-separator'></div>
            <div className='w-layout-hflex button-actions'>
              <button
                type='button'
                className='m button w-inline-block'
                onClick={onClose}
                disabled={enviando}
                style={{background: '#ccc', color: '#333', marginRight: '8px'}}
              >
                <div>Cancelar</div>
              </button>
              <button type='submit' className='m button w-inline-block' disabled={enviando}>
                <div>{labelBoton}</div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalBase>
  )
}

export default ModalContacto
